import { ErrorHandler, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { errorUrlTree, guardRoundTrip } from '@mkp/shared/util-guards';
import { isMenuRole, MenuRole } from '@mkp/account/models';
import { accountApiActions } from '@mkp/account/data-access/actions';
import { selectHasMenuRole } from '@mkp/account/state';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { permissionGuardActions } from '@mkp/core/state/actions';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, merge, Observable, of } from 'rxjs';

/**
 * `timeoutMs` is for a caller that has already spent part of the shared budget before this
 * guard starts, so the two waits add up to one deadline instead of two.
 */
export const permissionGuardFactory = (menuRole: MenuRole, timeoutMs?: number) => {
  return (): Observable<boolean | UrlTree> => {
    const store = inject(Store);
    const router = inject(Router);
    const actions$ = inject(Actions);
    const errorHandler = inject(ErrorHandler);

    const forbidden = () => errorUrlTree(router, 403);

    if (!menuRole || !isMenuRole(menuRole)) {
      // Throwing here produces a NavigationError that RouterEffects drops on the floor, so
      // the misconfiguration is reported and the user is sent to a page instead.
      errorHandler.handleError(
        new Error(`permissionGuard received no menu role (got ${String(menuRole)})`)
      );
      return of(forbidden());
    }

    return guardRoundTrip({
      guardName: 'permissionGuard',
      dispatchWhen$: store
        .select(selectWorkspaceFromUrlStatus)
        .pipe(filter((status) => status === WorkspaceFromUrlStatus.COMPLETED)),
      dispatchRequest: () => store.dispatch(permissionGuardActions.canActivate()),
      outcome$: merge(
        actions$.pipe(
          ofType(accountApiActions.refreshSelectedAccountSuccess),
          concatLatestFrom(() => store.select(selectHasMenuRole(menuRole))),
          // selectHasMenuRole answers undefined when no account is selected at all, which
          // dispatchWhen$ rules out. Treating it as a failure keeps an unreadable permission
          // state off the page rather than waving it through.
          map(([, hasMenuRole]) => (hasMenuRole === true ? true : forbidden()))
        ),
        // A 403 answers with refreshSelectedAccountNotFound and never reaches a success or a
        // failure action, so it is listed here rather than left to the timeout.
        actions$.pipe(
          ofType(
            accountApiActions.refreshSelectedAccountFailure,
            accountApiActions.refreshSelectedAccountNotFound
          ),
          map(() => forbidden())
        )
      ),
      onTimeout: forbidden,
      timeoutMs,
    });
  };
};
